import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Ship, Truck, ArrowLeft, Eye, Download, Printer } from "lucide-react";
import { getSharedPlan, downloadExcel, downloadPdf } from "@/lib/api";
import AllocationResult from "@/components/AllocationResult";
import TripSheet from "@/components/TripSheet";
import Logo from "@/components/Logo";

/**
 * Public read-only view of a load plan. Anyone with the token can open it —
 * no auth required. Drivers hit this URL on their phone at the dock.
 */
export default function SharedPlanPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getSharedPlan(token)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => {
        if (alive) setError(e?.response?.data?.detail || "Plan not found");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-600">
        <div className="mono text-sm">Loading plan…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="shared-plan-error">
        <div className="max-w-md text-center border border-slate-200 p-8">
          <div className="heading text-2xl font-semibold mb-2">Plan Not Found</div>
          <div className="text-sm text-[#64748B] mb-6">
            {error || "This link may have expired or been removed."}
          </div>
          <Link
            to="/"
            className="mono text-xs uppercase tracking-widest text-[#2563EB] hover:underline"
          >
            ← Back to CargoLoad Pro
          </Link>
        </div>
      </div>
    );
  }

  const { name, trip, brand, cases, vehicles, result, view_count } = data;

  const handleDownloadExcel = async () => {
    try {
      await downloadExcel({ name, result, cases, vehicles, trip, brand });
    } catch { toast.error("Download failed"); }
  };
  const handleDownloadPdf = async () => {
    try {
      await downloadPdf({ name, result, cases, vehicles, trip, brand });
    } catch { toast.error("PDF download failed"); }
  };
  const handlePrint = () => window.print();

  const hasCustomBrand = !!(brand?.logo_data_url || brand?.tagline || brand?.website);

  return (
    <div className="min-h-screen">
      {/* Public header — no history/settings, just view + downloads */}
      <header className="border-b border-slate-200 sticky top-0 z-30 bg-[#FFFFFF]/90 backdrop-blur-md print:hidden">
        <div className="px-4 md:px-10 py-3 md:py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 md:gap-3 group min-w-0">
            <Logo size={36} className="shrink-0" />
            <div className="min-w-0">
              <div className="heading text-base md:text-xl font-bold tracking-tight truncate">
                CARGOLOAD<span className="text-[#2563EB]">.PRO</span>
              </div>
              <div className="overline hidden sm:block">Shared Load Plan</div>
            </div>
          </Link>
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hidden sm:inline-flex items-center gap-1.5"
              data-testid="shared-view-count"
            >
              <Eye className="w-3 h-3" /> {view_count || 0} views
            </div>
            <button
              type="button"
              data-testid="shared-print-btn"
              onClick={handlePrint}
              className="mono text-xs uppercase tracking-widest text-slate-600 hover:text-[#2563EB] border border-slate-200 h-9 px-3 inline-flex items-center gap-1.5"
              title="Print this plan"
            >
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
            <button
              type="button"
              data-testid="shared-download-pdf-btn"
              onClick={handleDownloadPdf}
              className="mono text-xs uppercase tracking-widest text-slate-600 hover:text-[#2563EB] border border-slate-200 h-9 px-3 inline-flex items-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5" /> PDF
            </button>
            <button
              type="button"
              data-testid="shared-download-excel-btn"
              onClick={handleDownloadExcel}
              className="mono text-xs uppercase tracking-widest text-white bg-[#2563EB] hover:bg-[#1D4ED8] h-9 px-3 inline-flex items-center gap-1.5 font-semibold"
            >
              <Download className="w-3.5 h-3.5" /> Excel
            </button>
          </div>
        </div>
      </header>

      <main className="px-4 md:px-10 py-6 md:py-8 max-w-[1600px] mx-auto">
        {/* Plan meta banner */}
        <section className="border border-slate-200 bg-[#F8FAFC] p-5 mb-6" data-testid="shared-plan-meta">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="overline">Load Plan · Read-Only</div>
              <h1 className="heading text-2xl md:text-3xl font-semibold mt-1 break-words">
                {name}
              </h1>
              {hasCustomBrand && (
                <div className="mt-2 mono text-xs text-[#64748B]">
                  {brand.tagline && <span>{brand.tagline}</span>}
                  {brand.website && <span> · {brand.website}</span>}
                </div>
              )}
            </div>
            <Link
              to="/"
              className="mono text-xs uppercase tracking-widest text-slate-500 hover:text-[#2563EB] inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to app
            </Link>
          </div>

          {/* Trip + driver + dispatch info */}
          {(trip?.from_location || trip?.to_location || trip?.driver_name ||
            trip?.vehicle_number || trip?.scheduled_at) && (
            <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-3">
              {trip.from_location && (
                <div>
                  <div className="overline">From</div>
                  <div className="mono text-sm">{trip.from_location}</div>
                </div>
              )}
              {trip.to_location && (
                <div>
                  <div className="overline">To</div>
                  <div className="mono text-sm">{trip.to_location}</div>
                </div>
              )}
              {trip.driver_name && (
                <div>
                  <div className="overline">Driver</div>
                  <div className="mono text-sm">{trip.driver_name}</div>
                  {trip.driver_phone && (
                    <a href={`tel:${trip.driver_phone}`} className="mono text-xs text-[#2563EB] hover:underline">
                      {trip.driver_phone}
                    </a>
                  )}
                </div>
              )}
              {trip.vehicle_number && (
                <div>
                  <div className="overline">Truck #</div>
                  <div className="mono text-sm">{trip.vehicle_number}</div>
                </div>
              )}
              {trip.scheduled_at && (
                <div>
                  <div className="overline">Scheduled</div>
                  <div className="mono text-sm">{trip.scheduled_at.replace("T", " ")}</div>
                </div>
              )}
            </div>
          )}
        </section>

        <AllocationResult
          result={result}
          tripSheet={<TripSheet result={result} vehicles={vehicles} trip={trip} />}
        />
      </main>

      <footer className="mt-16 border-t border-slate-200 py-6 px-6 md:px-10 print:hidden">
        <div className="max-w-[1600px] mx-auto flex flex-col md:flex-row items-center justify-between gap-2">
          <div className="mono text-xs text-[#64748B]">
            Powered by CARGOLOAD.PRO — <Link to="/" className="text-[#2563EB] hover:underline">Plan your own loads</Link>
          </div>
          <div className="overline">Public read-only</div>
        </div>
      </footer>
    </div>
  );
}
